import { useEffect, useEffectEvent, useRef } from 'react';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { syntaxHighlighting } from '@codemirror/language';
import { Compartment, EditorState, type Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';

import { codeHighlightStyle, codeTheme, languageExtension } from '@/ui/codemirror-theme';
import classNames from '@/utils/classnames';

/**
 * An editable CodeMirror pane, themed to match the read-only {@link CodeViewer}.
 *
 * Uncontrolled by design. CodeMirror owns the document; `value` seeds it and is only pushed back
 * in when it differs from what the editor already holds — re-creating the view on every keystroke
 * would lose the cursor, the selection, and the undo history.
 */
export function CodeEditor({
  path,
  value,
  onChange,
  readOnly = false,
  className,
}: {
  /** Used only to pick the syntax mode. */
  path: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  // An Effect Event so the update listener never closes over a stale handler, which would otherwise
  // pin the first render's `onChange` for the life of the view.
  const emitChange = useEffectEvent(onChange);
  const readOnlyCompartment = useRef(new Compartment());

  // Rebuilt only when the language could have changed — not on every edit.
  useEffect(() => {
    if (!containerRef.current) return;

    const lang = languageExtension(path);
    const extensions: Extension[] = [
      lineNumbers(),
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap]),
      EditorView.lineWrapping,
      codeTheme,
      syntaxHighlighting(codeHighlightStyle),
      readOnlyCompartment.current.of([EditorState.readOnly.of(readOnly), EditorView.editable.of(!readOnly)]),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) emitChange(update.state.doc.toString());
      }),
      ...(lang ? [lang] : []),
    ];

    const view = new EditorView({
      state: EditorState.create({ doc: value, extensions }),
      parent: containerRef.current,
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Deliberately keyed on `path` alone. `value` seeds the initial document and is reconciled by
    // the effect below; including it here would tear down and rebuild the view on every keystroke,
    // losing the cursor, the selection and the undo history.
  }, [path]);

  // Push `value` in only when it genuinely differs — i.e. the parent replaced it (a different
  // file, a discarded edit), not when it is echoing back what the user just typed.
  useEffect(() => {
    const view = viewRef.current;
    if (!view || view.state.doc.toString() === value) return;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: value },
    });
  }, [value]);

  // Toggling read-only reconfigures in place, so the document and history survive.
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: readOnlyCompartment.current.reconfigure([
        EditorState.readOnly.of(readOnly),
        EditorView.editable.of(!readOnly),
      ]),
    });
  }, [readOnly]);

  return (
    <div
      ref={containerRef}
      className={classNames(
        'overflow-auto rounded-lg border border-line bg-surface-1 text-xs [&_.cm-editor]:h-full',
        className,
      )}
    />
  );
}
